import type { DiscoverySessionMessage } from '../../../api/discoveryAgent';

const DATA_IDEA_IDS_REGEX = /data-idea-ids="([^"]+)"/;
const MONGO_OBJECT_ID_REGEX = /^[a-fA-F0-9]{24}$/;

function processAliasIdsMap(
  mapData: unknown,
  targetMap: Record<string, string>
): void {
  if (!mapData) {
    return;
  }

  if (Array.isArray(mapData)) {
    for (const entry of mapData) {
      if (Array.isArray(entry) && entry.length === 2) {
        const [shortId, objectId] = entry;
        if (typeof shortId === 'string' && typeof objectId === 'string') {
          targetMap[shortId] = objectId;
        }
      }
    }
    return;
  }

  if (typeof mapData === 'object') {
    Object.assign(targetMap, mapData as Record<string, string>);
  }
}

function iterateAgentResponseEvents(response: unknown): unknown[] {
  if (!response || typeof response !== 'object') {
    return [];
  }

  const record = response as Record<string, unknown>;
  if (Array.isArray(record.events)) {
    return record.events;
  }
  if (Array.isArray(record)) {
    return response as unknown[];
  }
  return [response];
}

function getEventIdeaIdsMapData(event: unknown): unknown {
  if (!event || typeof event !== 'object') {
    return undefined;
  }
  const actions = (event as Record<string, unknown>).actions as Record<string, unknown> | undefined;
  const stateDelta =
    (actions?.state_delta as Record<string, unknown> | undefined) ??
    (actions?.stateDelta as Record<string, unknown> | undefined);
  return stateDelta?.idea_ids_map;
}

export function extractIdeaIdsMapFromFullResponse(fullResponse: unknown): Record<string, string> {
  const ideaIdsMap: Record<string, string> = {};
  for (const event of iterateAgentResponseEvents(fullResponse)) {
    const mapData = getEventIdeaIdsMapData(event);
    if (mapData) {
      processAliasIdsMap(mapData, ideaIdsMap);
    }
  }
  return ideaIdsMap;
}

export function replaceIdeaIdsInAgentText(
  text: string,
  ideaIdsMap: Record<string, string>
): string {
  if (!text || Object.keys(ideaIdsMap).length === 0) {
    return text;
  }

  const attributeRegex = /data-idea-ids="([^"]+)"/g;
  return text.replace(attributeRegex, (_match, idsString: string) => {
    const shortIds = idsString.split(',').map((id) => id.trim());
    const mappedIds = shortIds.map((shortId) => ideaIdsMap[shortId] ?? shortId);
    return `data-idea-ids="${mappedIds.join(',')}"`;
  });
}

export function extractRawIdeasListIdsFromContent(content: string): string[] {
  const match = content.match(DATA_IDEA_IDS_REGEX);
  if (!match?.[1]) {
    return [];
  }
  return match[1]
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
}

export function filterValidMongoObjectIds(ids: string[]): string[] {
  return ids.filter((id) => MONGO_OBJECT_ID_REGEX.test(id));
}

export function isValidMongoObjectId(value: string): boolean {
  return MONGO_OBJECT_ID_REGEX.test(value);
}

function readMessageIdeaIdsMap(message: DiscoverySessionMessage): Record<string, string> {
  const metadataMap = message.metadata?.ideaIdsMap;
  if (metadataMap && typeof metadataMap === 'object' && Object.keys(metadataMap).length > 0) {
    return metadataMap;
  }
  return extractIdeaIdsMapFromFullResponse(message.metadata?.fullResponse);
}

export function resolveExploreIdeaIdParam(
  aliasOrObjectId: string,
  messages: DiscoverySessionMessage[]
): string | null {
  const trimmed = aliasOrObjectId.trim();
  if (!trimmed) {
    return null;
  }
  if (isValidMongoObjectId(trimmed)) {
    return trimmed;
  }

  for (const message of messages) {
    const mapped = readMessageIdeaIdsMap(message)[trimmed];
    if (mapped && isValidMongoObjectId(mapped)) {
      return mapped;
    }
  }

  return null;
}

function readMessageIdeaIdsMapForMapping(message: DiscoverySessionMessage): Record<string, string> {
  return readMessageIdeaIdsMap(message);
}

export function applyIdeaIdMappingToMessageContent(
  content: string,
  message: Pick<DiscoverySessionMessage, 'metadata'>
): string {
  const ideaIdsMap = readMessageIdeaIdsMapForMapping(message as DiscoverySessionMessage);
  return replaceIdeaIdsInAgentText(content, ideaIdsMap);
}

function readMetadataIdeasListIds(message: DiscoverySessionMessage): string[] {
  const metadataIds = message.metadata?.ideasListIds;
  if (!Array.isArray(metadataIds) || metadataIds.length === 0) {
    return [];
  }
  return metadataIds.map((id) => String(id));
}

export function resolveExploreMessageIdeasListIds(message: DiscoverySessionMessage): string[] {
  const metadataIds = readMetadataIdeasListIds(message);
  if (metadataIds.length > 0) {
    return filterValidMongoObjectIds(metadataIds);
  }

  const mappedContent = applyIdeaIdMappingToMessageContent(message.content, message);
  return filterValidMongoObjectIds(extractRawIdeasListIdsFromContent(mappedContent));
}
